import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styles from './css/index.module.css';
import { formatDistanceToNow, format, isAfter, subWeeks } from 'date-fns';
import Select from 'react-select';
import { Dropdown } from 'react-bootstrap';

import { toast } from 'react-toastify';
import { /*DeleteProjectData*/ DuplicateProjectData, GetProjectData, PatchProjectData, selectProject, setProjectImg, setProjectDashboardView } from '../../redux/projectDataSlice';
import { buildDuplicatePlaceholder, isActiveDuplicateJob } from '../../utils/duplicateProjectJobs';
import { GetEmailPreferences } from '../../redux/usersSlice';
import { setDocs } from '../../redux/documentsSlice';
import { resetQestionBankData } from '../../redux/questionBankSlice';
// import { Dropdown } from 'react-bootstrap';
import 'react-circular-progressbar/dist/styles.css';
import { GetProjectUsers } from '../../redux/teamSlice';
import DoubleConfirmationModal from '../CommonComponents/DoubleConfirmationModal/DoubleConfirmationModal';
import Header from '../CommonComponents/Header';
import {
    Add16Regular,
    ListBar16Regular,
    GridKanban16Regular,
    Filter16Regular,
    Search16Regular,
    Grid16Regular,
    Dismiss16Regular,
    EyeOff16Regular,
    ArrowSort16Regular,
} from '@fluentui/react-icons';
import { Input } from '@fluentui/react-components';
import { resolveWorkspaceProjectTypeSelectOptions } from '../../utils/projectTypes';
import ProjectCard from './ProjectCard.js';
import ProjectTableView from './ProjectTableView.js';
import ProjectTaskStatus from './ProjectTaskStatus.js';
import KanbanBoard from './KanbanBoard.js';

const PROJECT_DASHBOARD_FILTERS_KEY = 'projectDashboardFilters';

const ProjectDashboard = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { data, projectDashboardView } = useSelector(state => state.projectData);
    const duplicateJobs = useSelector(state => state.projectData.duplicateJobs ?? []);
    // const { projectUsersAll } = useSelector(state => state.team);
    const [showArchive, setShowArchive] = React.useState(false);
    const [showDeleted, setShowDeleted] = React.useState(false);
    const [showTaskStatus, setShowTaskStatus] = React.useState(false);
    const { user } = useSelector(state => state.user);
    const memberships = useSelector(state => state?.user?.memberships || []);
    const activeWorkspaceId = useSelector(state => state?.user?.activeWorkspaceId) || localStorage.getItem('active_workspace_id');
    const userType = user?.types;
    const deleteModalRef = React.useRef();
    const duplicateModalRef = React.useRef();
    const [selectedTile, setTile] = React.useState();
    const [duplicateTile, setDuplicateTile] = React.useState(null);
    // Column visibility state for List (table) view
    const [showAllTableColumns, setShowAllTableColumns] = React.useState(true);
    const [hiddenTableColumns, setHiddenTableColumns] = React.useState([]);

    const getTableColumnVisibilityOptions = () => [
        { value: 'showAll', label: 'Show all' },
        { value: 'status', label: 'Status' },
        { value: 'project_name_type', label: 'Project Name & Type' },
        { value: 'project_deadline', label: 'Project Deadline' },
        { value: 'target_investor', label: 'Target & Investor' },
        { value: 'task_status', label: 'Task Status' },
        { value: 'key_task', label: 'Key Task' },
        { value: 'last_updated', label: 'Last updated' },
    ];

    const handleTableShowAllColumns = checked => {
        if (checked) {
            setShowAllTableColumns(true);
            setHiddenTableColumns([]);
        } else {
            setShowAllTableColumns(false);
        }
    };

    const handleTableColumnVisibility = (column, checked) => {
        if (column === 'showAll') {
            handleTableShowAllColumns(checked);
            return;
        }
        if (checked) {
            setHiddenTableColumns(prev => prev.filter(col => col !== column));
            if (hiddenTableColumns.length === 0) setShowAllTableColumns(true);
        } else {
            setHiddenTableColumns(prev => [...prev, column]);
            setShowAllTableColumns(false);
        }
    };

    const isTableColumnVisible = column => {
        if (showAllTableColumns) return true;
        return !hiddenTableColumns.includes(column);
    };

    // Sort state for list view
    const [listSortColumn, setListSortColumn] = React.useState('');
    const [listSortOrder, setListSortOrder] = React.useState(''); // 'asc' | 'desc'
    const handleClearListSort = () => {
        setListSortColumn('');
        setListSortOrder('');
    };

    const getListSortColumnOptions = () => [
        { value: 'status', label: 'Status' },
        { value: 'project_name', label: 'Project Name' },
        { value: 'project_type', label: 'Project Type' },
        { value: 'project_deadline', label: 'Project Deadline' },
        { value: 'company_name', label: 'Company' },
        { value: 'investor_name', label: 'Investor' },
        { value: 'last_updated', label: 'Last updated' },
    ];
    const getListSortOrderOptions = () => [
        { value: 'asc', label: 'Ascending' },
        { value: 'desc', label: 'Descending' },
    ];

    const [filters, setFilters] = React.useState({
        status: [],
        projectType: [],
        search: '',
    });

    // Load persisted filters & preferences on first render
    React.useEffect(() => {
        try {
            const stored = localStorage.getItem(PROJECT_DASHBOARD_FILTERS_KEY);
            if (!stored) return;

            const parsed = JSON.parse(stored);

            if (parsed.filters) {
                setFilters(prev => ({
                    ...prev,
                    ...parsed.filters,
                    status: Array.isArray(parsed.filters.status) ? parsed.filters.status : [],
                    projectType: Array.isArray(parsed.filters.projectType) ? parsed.filters.projectType : [],
                    search: typeof parsed.filters.search === 'string' ? parsed.filters.search : '',
                }));
            }

            if (typeof parsed.showArchive === 'boolean') {
                setShowArchive(parsed.showArchive);
            }
            if (typeof parsed.showDeleted === 'boolean') {
                setShowDeleted(parsed.showDeleted);
            }

            if (typeof parsed.showAllTableColumns === 'boolean') {
                setShowAllTableColumns(parsed.showAllTableColumns);
            }
            if (Array.isArray(parsed.hiddenTableColumns)) {
                setHiddenTableColumns(parsed.hiddenTableColumns);
            }

            if (typeof parsed.listSortColumn === 'string') {
                setListSortColumn(parsed.listSortColumn);
            }
            if (typeof parsed.listSortOrder === 'string') {
                setListSortOrder(parsed.listSortOrder);
            }
        } catch (error) {
            // Fail silently if parsing fails
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Persist filters & related preferences whenever they change
    React.useEffect(() => {
        try {
            const payload = {
                filters,
                showArchive,
                showDeleted,
                showAllTableColumns,
                hiddenTableColumns,
                listSortColumn,
                listSortOrder,
            };
            localStorage.setItem(PROJECT_DASHBOARD_FILTERS_KEY, JSON.stringify(payload));
        } catch (error) {
            // Fail silently if storage is unavailable
        }
    }, [filters, showArchive, showDeleted, showAllTableColumns, hiddenTableColumns, listSortColumn, listSortOrder]);

    const applyListSorting = data => {
        if (!listSortColumn || !listSortOrder) return data;
        const sorted = [...data].sort((a, b) => {
            const getVal = p => {
                switch (listSortColumn) {
                    case 'status':
                        return p?.status || '';
                    case 'project_name':
                        return p?.project_name || '';
                    case 'project_type':
                        return p?.project_type || '';
                    case 'project_deadline':
                        return p?.project_deadline ? new Date(p.project_deadline) : new Date(0);
                    case 'company_name':
                        return p?.company_name || '';
                    case 'investor_name':
                        return p?.investor_name || '';
                    case 'last_updated':
                        return p?.projectprogress?.last_updated ? new Date(p.projectprogress.last_updated) : new Date(0);
                    default:
                        return '';
                }
            };
            let av = getVal(a);
            let bv = getVal(b);
            if (typeof av === 'string') av = av.toLowerCase();
            if (typeof bv === 'string') bv = bv.toLowerCase();
            if (listSortOrder === 'asc') return av > bv ? 1 : -1;
            return av < bv ? 1 : -1;
        });
        return sorted;
    };

    // Compute filtered then sorted projects for list view

    const duplicatePlaceholders = React.useMemo(() => {
        if (showArchive || showDeleted) return [];
        return duplicateJobs.filter(isActiveDuplicateJob).map(buildDuplicatePlaceholder);
    }, [duplicateJobs, showArchive, showDeleted]);

    const filteredProjects = React.useMemo(() => {
        const base = getFilteredProjects();
        const sorted = applyListSorting(base);
        return [...duplicatePlaceholders, ...sorted];
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, filters, showArchive, showDeleted, listSortColumn, listSortOrder, duplicatePlaceholders]);

    const activeWorkspace = React.useMemo(() => {
        const m = memberships.find(mem => mem?.workspace?.id === activeWorkspaceId);
        return m?.workspace || null;
    }, [memberships, activeWorkspaceId]);

    const fallbackStatusOptions = React.useMemo(
        () => [
            { value: 'Discussing', label: 'Discussing', color: '#7FB3FF' },
            { value: 'Scoping', label: 'Scoping', color: '#4F89FF' },
            { value: 'Proposal Sent', label: 'Proposal Sent', color: '#B88BFF' },
            { value: 'Active', label: 'Active', color: '#2EE59D' },
            { value: 'Report Issued', label: 'Report Issued', color: '#F2C94C' },
            { value: 'Closed', label: 'Closed', color: '#FFB26B' },
            { value: 'Invoicing', label: 'Invoicing', color: '#006400' },
            { value: 'On Hold', label: 'On Hold', color: '#AAAAAA' },
            { value: 'Lost', label: 'Lost', color: '#AAA' },
        ],
        [],
    );

    const statusOptions = React.useMemo(() => {
        const opts = activeWorkspace?.project_status_options;
        const base =
            Array.isArray(opts) && opts.length
                ? opts
                      .filter(o => o && o.name)
                      .map(o => ({
                          value: o.name,
                          label: o.name,
                          color: o.color || '#AAAAAA',
                      }))
                : fallbackStatusOptions;

        // Ensure Undefined exists for projects without a status.
        const hasUndefined = base.some(o => o.value === 'Undefined');
        return hasUndefined ? base : [...base, { value: 'Undefined', label: 'Undefined', color: '#AAAAAA' }];
    }, [activeWorkspace, fallbackStatusOptions]);

    // Use Redux state for active view instead of local state
    const activeView = projectDashboardView || 'kanban';

    // Ensure view preference is initialized
    React.useEffect(() => {
        if (!projectDashboardView) {
            const savedView = localStorage.getItem('projectDashboardView') || 'kanban';
            dispatch(setProjectDashboardView(savedView));
        }
    }, [projectDashboardView, dispatch]);

    // Calculate counts for status options
    const getStatusOptionsWithCounts = () => {
        if (!data) return statusOptions;

        const statusCounts = {};

        // Count projects for each status
        data.forEach(item => {
            if (!item || !item.projectprogress) return;

            // Apply archive/deleted filters
            let shouldCount = false;
            if (showDeleted && item?.soft_delete === true) {
                shouldCount = true;
            } else if (showArchive && item?.archive === true && item?.soft_delete !== true) {
                shouldCount = true;
            } else if (!showDeleted && !showArchive && !item?.archive && !item?.soft_delete) {
                shouldCount = true;
            }

            if (shouldCount) {
                const status = item.status || 'Undefined';
                statusCounts[status] = (statusCounts[status] || 0) + 1;
            }
        });

        return statusOptions.map(option => ({
            ...option,
            label: `${option.label} (${statusCounts[option.value] || 0})`,
        }));
    };

    // Calculate counts for project type options
    const getProjectTypeOptionsWithCounts = () => {
        if (!data) return projectTypes;

        const projectTypeCounts = {};

        // Count projects for each project type
        data.forEach(item => {
            if (!item || !item.projectprogress) return;

            // Apply archive/deleted filters
            let shouldCount = false;
            if (showDeleted && item?.soft_delete === true) {
                shouldCount = true;
            } else if (showArchive && item?.archive === true && item?.soft_delete !== true) {
                shouldCount = true;
            } else if (!showDeleted && !showArchive && !item?.archive && !item?.soft_delete) {
                shouldCount = true;
            }

            if (shouldCount) {
                const projectType = item.project_type;
                if (projectType) {
                    projectTypeCounts[projectType] = (projectTypeCounts[projectType] || 0) + 1;
                }
            }
        });

        return projectTypes.map(option => ({
            ...option,
            label: `${option.label} (${projectTypeCounts[option.value] || 0})`,
        }));
    };

    const projectTypes = React.useMemo(() => resolveWorkspaceProjectTypeSelectOptions(activeWorkspace), [activeWorkspace]);

    const kanbanStatuses = React.useMemo(() => {
        const base = statusOptions.map(o => o.value).filter(Boolean);
        return base.length ? base : ['Undefined'];
    }, [statusOptions]);

    // Filter projects based on current filters
    function getFilteredProjects() {
        if (!data) return [];

        return data.filter(item => {
            if (!item || !item.projectprogress) return false;

            // Apply archive/deleted filters
            if (showDeleted && item?.soft_delete === true) {
                // Continue with other filters
            } else if (showArchive && item?.archive === true && item?.soft_delete !== true) {
                // Continue with other filters
            } else if (!showDeleted && !showArchive && !item?.archive && !item?.soft_delete) {
                // Continue with other filters
            } else {
                return false;
            }

            // Apply status filter
            if (
                filters.status.length > 0 &&
                !filters.status.some(option => {
                    if (option.value === 'Undefined') {
                        return !item.status || item.status === null || item.status === '';
                    }
                    return option.value === item.status;
                })
            )
                return false;

            // Apply project type filter
            if (filters.projectType.length > 0 && !filters.projectType.some(option => option.value === item.project_type)) return false;

            // Apply search filter
            if (filters.search) {
                const searchTerm = filters.search.toLowerCase();
                const searchableFields = [item.project_name, item.company_name, item.key_investor_name, item.investor_organisation, item.project_type].join(' ').toLowerCase();

                if (!searchableFields.includes(searchTerm)) return false;
            }

            return true;
        });
    }

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    const clearFilters = () => {
        setFilters({
            status: [],
            projectType: [],
            search: '',
        });
    };

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            borderColor: 'var(--Neutral-Gray-5)',
            borderBottomColor: 'var(--Neutral-Gray-6)',
            minHeight: '32px',
            fontSize: 14,
            boxShadow: state.isFocused ? '0 0 0 1px var(--Brand-Secondary)' : 'none',
        }),
        valueContainer: (provided, state) => ({
            ...provided,
            height: 'auto',
            minHeight: '32px',
            padding: '2px 6px',
            flexWrap: 'wrap',
        }),
        input: (provided, state) => ({
            ...provided,
            margin: '0px',
        }),
        indicatorSeparator: state => ({
            display: 'none',
        }),
        indicatorsContainer: (provided, state) => ({
            ...provided,
            height: '32px',
        }),
        multiValue: (provided, state) => ({
            ...provided,
            backgroundColor: 'var(--Neutral-Gray-5)',
            color: 'var(--Neutral-Gray-8)',
            margin: '1px',
        }),
        multiValueLabel: (provided, state) => ({
            ...provided,
            color: 'var(--Neutral-Gray-8)',
            fontSize: '12px',
        }),
        multiValueRemove: (provided, state) => ({
            ...provided,
            color: 'var(--Neutral-Gray-8)',
            ':hover': {
                backgroundColor: 'var(--Neutral-Gray-6)',
                color: 'var(--Neutral-Gray-9)',
            },
        }),
        menu: (provided, state) => ({
            ...provided,
            zIndex: 9999,
        }),
        option: (provided, state) => ({
            ...provided,
            backgroundColor: state.isSelected ? 'var(--Neutral-Gray-5)' : state.isFocused ? 'var(--Neutral-Gray-3)' : 'white',
            color: state.isSelected ? 'var(--Neutral-Gray-9)' : 'var(--Neutral-Gray-8)',
            fontWeight: 400,
        }),
    };

    React.useEffect(() => {
        dispatch(GetProjectData());
        dispatch(GetEmailPreferences());
    }, [dispatch]);

    const handleChangeProject = async id => {
        localStorage.setItem('project_id', id);
        dispatch(
            selectProject({
                id,
                callback: () => handleNavigate(id),
                userType: userType,
            }),
        );
        dispatch(resetQestionBankData()); // Reset IRL AI responses when changing projects
        await dispatch(GetProjectUsers());
        // if(res?.type==='GET_PROJECT_USERS/fulfilled'){

        // }
    };
    const handleNavigate = id => {
        const currentProject = data?.find(item => item?.id === id);
        if (user?.has_diligence_tools) {
            navigate('/overview');
            // if (currentProject?.project_deadline) navigate('/overview');
            // else navigate('/AddProjectDetails', { state: { page: 'db', navigateTo: '/overview' } });
        } else {
            const userData = currentProject?.vdr?.nda?.signees?.find(item => item?.user === user?.id);
            if (userData?.nda_signed === false) {
                navigate('/nda');
            }
            // else if (userData?.tandc === false) navigate('/tc');
            else {
                navigate('/dataroom');
            }
        }
    };
    const handleEditProject = async id => {
        localStorage.setItem('project_id', id);
        dispatch(selectProject({ id }));
        dispatch(setProjectImg(''));
        navigate('/EditProject');
    };
    const resolveProjectId = projectOrId => {
        if (typeof projectOrId === 'object' && projectOrId !== null) {
            return projectOrId.id;
        }
        return projectOrId;
    };

    const handleDeleteProject = async projectOrId => {
        const id = resolveProjectId(projectOrId);
        if (!id || String(id).startsWith('duplicate-job-')) {
            toast.error('Unable to delete project. Please refresh and try again.');
            return;
        }
        await dispatch(PatchProjectData({ data: { soft_delete: true }, id }));
        setTile();
    };
    const handleAddProject = () => {
        navigate('/AddProjectDetails');
        dispatch(setProjectImg(''));
        dispatch(selectProject({ id: null }));
        dispatch(setDocs([]));
    };
    const handleArchiveProject = async item => {
        dispatch(PatchProjectData({ data: { archive: !item?.archive }, id: item?.id }));
    };
    const handleDuplicateProject = async item => {
        if (!item?.id) return;
        setDuplicateTile(null);
        try {
            await dispatch(
                DuplicateProjectData({
                    id: item.id,
                    projectName: `Copy of ${item.project_name}`,
                    sourceProject: item,
                })
            ).unwrap();
        } catch {
            // Toast handled in slice
        }
    };
    const openDuplicateConfirm = item => {
        setDuplicateTile(item);
        duplicateModalRef?.current?.click();
    };

    // let currentUser = projectUsersAll?.find(item => item?.value?.id === user?.id);

    function getTotalFileCount(data) {
        let totalFiles = 0;
        for (const workstream in data) {
            totalFiles += data?.[workstream]?.files;
        }
        return totalFiles;
    }
    const calculateColorSM = value => {
        if (value === 0) {
            return 'var(--Neutral-Gray-6)';
        }
        if (value > 0 && value <= 15) {
            return '#B00020';
        }
        if (value >= 16 && value <= 35) {
            return '#FF4E11';
        }
        if (value >= 36 && value <= 50) {
            return '#FF8E15';
        }
        if (value >= 51 && value <= 65) {
            return '#FAB733';
        }
        if (value >= 66 && value <= 80) {
            return '#ACB334';
        }
        if (value >= 81 && value <= 100) {
            return '#69B34C';
        }
    };

    const getStatusColor = status => {
        const statusOption = statusOptions.find(option => option.value === status);
        return statusOption?.color || '#AAAAAA';
    };

    const formatLastUpdated = timestamp => {
        if (!timestamp) return '--';
        const date = new Date(timestamp);
        const oneWeekAgo = subWeeks(new Date(), 1);

        if (isAfter(date, oneWeekAgo)) {
            return formatDistanceToNow(date, { addSuffix: true });
        } else {
            return format(date, 'MMM d, yyyy');
        }
    };

    const convertHtmlToText = html => {
        if (!html) return '--';
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = html;
        return tempDiv.textContent || tempDiv.innerText || '--';
    };

    const handleStatusChange = (projectId, newStatus) => {
        if (newStatus === 'Undefined') {
            return dispatch(PatchProjectData({ data: { status: null }, id: projectId }));
        } else {
            return dispatch(PatchProjectData({ data: { status: newStatus }, id: projectId }));
        }
    };

    return (
        <div>
            <Header hideProjectItems={true} />
            <div className={styles.body}>
                {!showTaskStatus && (
                    <div className={styles.headerSection + ' d-flex flex-wrap align-items-center justify-content-between mt-4'}>
                        <div className={styles.headerLeft + ' d-flex align-items-center flex-wrap'}>
                            {showArchive || showDeleted ? (
                                <div>
                                    <p className={styles.nonActiveLabel}>{showDeleted ? 'Deleted' : 'Archived'} Projects</p>
                                    {showDeleted && <p className={styles.deletedText}>Projects in this folder will be permanently removed every 15 days.</p>}
                                </div>
                            ) : (
                                <>
                                    <button onClick={handleAddProject} className={styles.addProjectBtn}>
                                        <Add16Regular className="me-1" />
                                        New Project
                                    </button>
                                    {activeView === 'tile' && (
                                        <div className={styles.viewToggleContainer}>
                                            <div
                                                className={`d-flex align-items-center ms-3 cursor-pointer ${styles.viewToggleButton}`}
                                                onClick={() => dispatch(setProjectDashboardView('kanban'))}>
                                                <GridKanban16Regular color="var(--Brand-Secondary)" />
                                                <span className={styles.archiveBtnText + ' ms-1'}>Kanban View</span>
                                            </div>
                                            <div
                                                className={`d-flex align-items-center ms-3 cursor-pointer ${styles.viewToggleButton}`}
                                                onClick={() => dispatch(setProjectDashboardView('list'))}>
                                                <ListBar16Regular color="var(--Brand-Secondary)" />
                                                <span className={styles.archiveBtnText + ' ms-1'}>List View</span>
                                            </div>
                                        </div>
                                    )}
                                    {activeView === 'kanban' && (
                                        <div className={styles.viewToggleContainer}>
                                            <div
                                                style={{ width: '120px' }}
                                                className={`d-flex align-items-center ms-3 cursor-pointer ${styles.viewToggleButton}`}
                                                onClick={() => dispatch(setProjectDashboardView('tile'))}>
                                                <Grid16Regular color="var(--Brand-Secondary)" />
                                                <span className={styles.archiveBtnText + ' ms-1'}>Project View</span>
                                            </div>
                                            <div
                                                style={{ width: '120px' }}
                                                className={`d-flex align-items-center ms-3 cursor-pointer ${styles.viewToggleButton}`}
                                                onClick={() => dispatch(setProjectDashboardView('list'))}>
                                                <ListBar16Regular color="var(--Brand-Secondary)" />
                                                <span className={styles.archiveBtnText + ' ms-1'}>List View</span>
                                            </div>
                                        </div>
                                    )}
                                    {activeView === 'list' && (
                                        <div className={styles.viewToggleContainer}>
                                            <div
                                                className={`d-flex align-items-center ms-3 cursor-pointer ${styles.viewToggleButton}`}
                                                onClick={() => dispatch(setProjectDashboardView('tile'))}>
                                                <Grid16Regular color="var(--Brand-Secondary)" />
                                                <span className={styles.archiveBtnText + ' ms-1'}>Project View</span>
                                            </div>
                                            <div
                                                className={`d-flex align-items-center ms-3 cursor-pointer ${styles.viewToggleButton}`}
                                                onClick={() => dispatch(setProjectDashboardView('kanban'))}>
                                                <GridKanban16Regular color="var(--Brand-Secondary)" />
                                                <span className={styles.archiveBtnText + ' ms-1'}>Kanban View</span>
                                            </div>
                                        </div>
                                    )}
                                    <Dropdown>
                                        <Dropdown.Toggle variant="link" className={styles.filterButton} style={{ textDecoration: 'none', color: 'var(--Brand-Secondary)' }}>
                                            <Filter16Regular color="var(--Neutral-Gray-8)" />
                                            <span className={styles.archiveBtnText + ' ms-1'}>Filter</span>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu className={styles.filterDropdown}>
                                            <div className="p-3">
                                                <div className="mb-3">
                                                    <label className={styles.filterLabel}>Status</label>
                                                    <Select
                                                        isMulti
                                                        name="status"
                                                        options={getStatusOptionsWithCounts()}
                                                        value={filters.status}
                                                        onChange={value => handleFilterChange('status', value)}
                                                        styles={selectStyles}
                                                        closeMenuOnSelect={false}
                                                        hideSelectedOptions={false}
                                                        placeholder="Select statuses..."
                                                    />
                                                </div>
                                                <div className="mb-3">
                                                    <label className={styles.filterLabel}>Project Type</label>
                                                    <Select
                                                        isMulti
                                                        name="projectType"
                                                        options={getProjectTypeOptionsWithCounts()}
                                                        value={filters.projectType}
                                                        onChange={value => handleFilterChange('projectType', value)}
                                                        styles={selectStyles}
                                                        closeMenuOnSelect={false}
                                                        hideSelectedOptions={false}
                                                        placeholder="Select project types..."
                                                    />
                                                </div>
                                                <div className="d-flex justify-content-end">
                                                    <button className="btn btn-outline-secondary btn-sm" onClick={clearFilters}>
                                                        Clear Filters
                                                    </button>
                                                </div>
                                            </div>
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    {activeView === 'list' && (
                                        <>
                                            <Dropdown>
                                                <Dropdown.Toggle variant="link" className={styles.filterButton} style={{ textDecoration: 'none', color: 'var(--Brand-Secondary)' }}>
                                                    <EyeOff16Regular color="var(--Neutral-Gray-8)" />
                                                    <span className={styles.archiveBtnText + ' ms-1'}>Hide</span>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className={styles.filterDropdown} style={{ minWidth: '200px' }}>
                                                    <div className="p-3">
                                                        <h6 className={styles.filterLabel + ' mb-3'}>Display Columns</h6>
                                                        {getTableColumnVisibilityOptions().map(option => (
                                                            <div key={option.value} className="d-flex align-items-center mb-2">
                                                                <input
                                                                    type="checkbox"
                                                                    style={{ width: '16px', height: '16px', marginRight: '8px', cursor: 'pointer' }}
                                                                    checked={option.value === 'showAll' ? showAllTableColumns : isTableColumnVisible(option.value)}
                                                                    onChange={e => handleTableColumnVisibility(option.value, e.target.checked)}
                                                                />
                                                                <span className={styles.menuregularText}>{option.label}</span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                            <Dropdown>
                                                <Dropdown.Toggle variant="link" className={styles.filterButton} style={{ textDecoration: 'none', color: 'var(--Brand-Secondary)' }}>
                                                    <ArrowSort16Regular color="var(--Neutral-Gray-8)" />
                                                    <span className={styles.archiveBtnText + ' ms-1'}>Sort</span>
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu className={styles.filterDropdown} style={{ minWidth: '350px' }}>
                                                    <div className="p-3">
                                                        <div className="d-flex align-items-center justify-content-between mb-3">
                                                            <h6 className={styles.filterLabel + ' mb-0'}>Sort Items By</h6>
                                                            {(listSortColumn || listSortOrder) && (
                                                                <span className={styles.secondaryBtnText} onClick={handleClearListSort} style={{ cursor: 'pointer' }}>
                                                                    Clear
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="d-flex align-items-center gap-2">
                                                            <select
                                                                className={'form-select form-select-sm ' + styles.filterSelect}
                                                                value={listSortColumn}
                                                                onChange={e => setListSortColumn(e.target.value)}>
                                                                <option value="">Select column</option>
                                                                {getListSortColumnOptions().map(option => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                            <select
                                                                className={'form-select form-select-sm ' + styles.filterSelect}
                                                                value={listSortOrder}
                                                                onChange={e => setListSortOrder(e.target.value)}
                                                                disabled={!listSortColumn}>
                                                                <option value="">Select sorting</option>
                                                                {getListSortOrderOptions().map(option => (
                                                                    <option key={option.value} value={option.value}>
                                                                        {option.label}
                                                                    </option>
                                                                ))}
                                                            </select>
                                                        </div>
                                                    </div>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </>
                                    )}
                                </>
                            )}
                        </div>
                        <div className={styles.headerRight + ' d-flex align-items-center flex-wrap justify-content-end mt-3 mt-md-0'}>
                            <Input
                                contentBefore={<Search16Regular />}
                                contentAfter={filters.search && <Dismiss16Regular style={{ cursor: 'pointer' }} onClick={() => handleFilterChange('search', '')} />}
                                className={styles.searchInput}
                                placeholder="Search Projects"
                                value={filters.search}
                                onChange={e => handleFilterChange('search', e.target.value)}
                            />
                            <span
                                onClick={() => {
                                    setShowArchive(!showArchive);
                                    setShowDeleted(false);
                                    dispatch(setProjectDashboardView('tile'));
                                }}
                                className={styles.archiveBtnText}>
                                View {showArchive ? 'Active' : 'Archived'} Projects
                            </span>
                            <span
                                onClick={() => {
                                    setShowDeleted(!showDeleted);
                                    setShowArchive(false);
                                    dispatch(setProjectDashboardView('tile'));
                                }}
                                className={styles.archiveBtnText}>
                                View {showDeleted ? 'Active' : 'Deleted'} Projects
                            </span>
                            {/* <span
                                onClick={() => {
                                    setShowTaskStatus(!showTaskStatus);
                                    setShowArchive(false);
                                    setShowDeleted(false);
                                }}
                                className={styles.archiveBtnText}
                                style={{ cursor: 'pointer' }}>
                                Project Status
                            </span> */}
                        </div>
                    </div>
                )}

                {/* Hide other views when task status is shown */}
                {!showTaskStatus && activeView === 'tile' && (
                    <div className="row mt-5">
                        {filteredProjects.map(item => {
                            if (!item || !item.projectprogress) return null;
                            // Condition: Show all projects with soft_delete === true if showDeleted is true
                            if (showDeleted && item?.soft_delete === true) {
                                return (
                                    <div className="col-sm-12 col-md-6 col-lg-3">
                                        <ProjectCard
                                            key={item?.id}
                                            item={item}
                                            user={user}
                                            handleEditProject={handleEditProject}
                                            handleArchiveProject={handleArchiveProject}
                                            openDuplicateConfirm={openDuplicateConfirm}
                                            duplicateJobs={duplicateJobs}
                                            setTile={setTile}
                                            deleteModalRef={deleteModalRef}
                                            handleChangeProject={handleChangeProject}
                                            getStatusColor={getStatusColor}
                                            statusOptions={statusOptions}
                                            calculateColorSM={calculateColorSM}
                                            formatLastUpdated={formatLastUpdated}
                                            convertHtmlToText={convertHtmlToText}
                                            getTotalFileCount={getTotalFileCount}
                                        />
                                    </div>
                                );
                            }
                            // Condition: Show all projects with archive === true except those with soft_delete === true if showArchive is true
                            else if (showArchive && item?.archive === true && item?.soft_delete !== true) {
                                return (
                                    <div className="col-sm-12 col-md-6 col-lg-3">
                                        <ProjectCard
                                            key={item?.id}
                                            item={item}
                                            user={user}
                                            handleEditProject={handleEditProject}
                                            handleArchiveProject={handleArchiveProject}
                                            openDuplicateConfirm={openDuplicateConfirm}
                                            duplicateJobs={duplicateJobs}
                                            setTile={setTile}
                                            deleteModalRef={deleteModalRef}
                                            handleChangeProject={handleChangeProject}
                                            getStatusColor={getStatusColor}
                                            statusOptions={statusOptions}
                                            calculateColorSM={calculateColorSM}
                                            formatLastUpdated={formatLastUpdated}
                                            convertHtmlToText={convertHtmlToText}
                                            getTotalFileCount={getTotalFileCount}
                                        />
                                    </div>
                                );
                            }
                            // Condition: Show the card if neither showDeleted nor showArchive is true
                            else if (!showDeleted && !showArchive && !item?.archive && !item?.soft_delete) {
                                return (
                                    <div className="col-sm-12 col-md-6 col-lg-3">
                                        <ProjectCard
                                            key={item?.id}
                                            item={item}
                                            user={user}
                                            handleEditProject={handleEditProject}
                                            handleArchiveProject={handleArchiveProject}
                                            openDuplicateConfirm={openDuplicateConfirm}
                                            duplicateJobs={duplicateJobs}
                                            setTile={setTile}
                                            deleteModalRef={deleteModalRef}
                                            handleChangeProject={handleChangeProject}
                                            getStatusColor={getStatusColor}
                                            statusOptions={statusOptions}
                                            calculateColorSM={calculateColorSM}
                                            formatLastUpdated={formatLastUpdated}
                                            convertHtmlToText={convertHtmlToText}
                                            getTotalFileCount={getTotalFileCount}
                                        />
                                    </div>
                                );
                            }
                            // Return null for other cases
                            return null;
                        })}
                    </div>
                )}
                {!showTaskStatus && activeView === 'kanban' && (
                    <div className="mt-5 text-center text-muted">
                        <KanbanBoard
                            projects={filteredProjects}
                            onStatusChange={handleStatusChange}
                            user={user}
                            handleEditProject={handleEditProject}
                            handleArchiveProject={handleArchiveProject}
                            openDuplicateConfirm={openDuplicateConfirm}
                            duplicateJobs={duplicateJobs}
                            setTile={setTile}
                            deleteModalRef={deleteModalRef}
                            handleChangeProject={handleChangeProject}
                            getStatusColor={getStatusColor}
                            statusOptions={[...statusOptions, { value: 'Undefined', label: 'Undefined', color: '' }]}
                            kanbanStatuses={kanbanStatuses}
                            calculateColorSM={calculateColorSM}
                            formatLastUpdated={formatLastUpdated}
                            convertHtmlToText={convertHtmlToText}
                            getTotalFileCount={getTotalFileCount}
                        />
                    </div>
                )}
                {!showTaskStatus && activeView === 'list' && (
                    <div className="mt-5">
                        <ProjectTableView
                            projects={filteredProjects}
                            onStatusChange={handleStatusChange}
                            onEdit={handleEditProject}
                            onDelete={handleDeleteProject}
                            onArchive={handleArchiveProject}
                            onUnarchive={handleArchiveProject}
                            openDuplicateConfirm={openDuplicateConfirm}
                            duplicateJobs={duplicateJobs}
                            handleChangeProject={handleChangeProject}
                            formatLastUpdated={formatLastUpdated}
                            user={user}
                            calculateColorSM={calculateColorSM}
                            isTableColumnVisible={isTableColumnVisible}
                            deleteModalRef={deleteModalRef}
                            handleEditProject={handleEditProject}
                            handleArchiveProject={handleArchiveProject}
                            setTile={setTile}
                            getStatusColor={getStatusColor}
                        />
                    </div>
                )}
                {showTaskStatus && (
                    <div className="">
                        <ProjectTaskStatus
                            getStatusColor={getStatusColor}
                            formatLastUpdated={formatLastUpdated}
                            setShowTaskStatus={setShowTaskStatus}
                            statusOptions={statusOptions}
                            projectTypes={projectTypes}
                        />
                    </div>
                )}
            </div>
            <DoubleConfirmationModal
                handleAction={() => handleDeleteProject(selectedTile)}
                message={'Deleting a Project will REMOVE all associated Files, Data Room content, Information Requests, Comments, Users, PERMANENTLY from Lens.'}>
                <div ref={deleteModalRef} />
            </DoubleConfirmationModal>
            <DoubleConfirmationModal
                type="warning"
                handleAction={() => handleDuplicateProject(duplicateTile)}
                message={`Creates a copy named Copy of ${duplicateTile?.project_name || 'this project'}. Team members and Lens Intelligence will not be copied.`}
                modalOneBtnL="Duplicate"
                modalOneBtnR="Cancel"
                modalTwoBtnL="Cancel"
                modalTwoBtnR="Duplicate">
                <div ref={duplicateModalRef} />
            </DoubleConfirmationModal>
        </div>
    );
};

export default ProjectDashboard;
